/**
 * Cache strategy implementations
 */

import type {
  ICacheEntry,
  ICacheMetadata,
  TCacheStrategy,
} from '../webrequest.types.js';
import { CacheStore } from './cache.store.js';
import {
  extractCacheMetadata,
  isFresh,
  requiresRevalidation,
  createConditionalHeaders,
  headersToObject,
} from './cache.headers.js';
import { isWebrequestTimeoutError } from '../utils/timeout.js';

export interface IStrategyContext {
  request: Request;
  cacheKey: string;
  cacheStore: CacheStore;
  fetchFn: (request: Request) => Promise<Response>;
  logging?: boolean;
  signal?: AbortSignal;
  cacheMaxAge?: number;
  cacheMaxEntries: number;
  scheduleBackgroundRevalidation: (
    operationArg: (signalArg: AbortSignal) => Promise<void>,
  ) => void;
}

export interface IStrategyResult {
  response: Response;
  fromCache: boolean;
  revalidated: boolean;
}

/**
 * Base strategy handler interface
 */
export interface ICacheStrategyHandler {
  execute(context: IStrategyContext): Promise<IStrategyResult>;
}

/**
 * Network-First Strategy
 * Try network first, fallback to cache on failure
 */
export class NetworkFirstStrategy implements ICacheStrategyHandler {
  async execute(context: IStrategyContext): Promise<IStrategyResult> {
    let response: Response | undefined;
    try {
      // Try network first
      response = await context.fetchFn(context.request);

      // If successful, cache it
      if (response.ok) {
        await this.cacheResponse(context, response);
      }

      return {
        response,
        fromCache: false,
        revalidated: false,
      };
    } catch (error) {
      await cancelDiscardedResponse(
        response,
        error,
        context.request.signal,
      );
      if (context.signal?.aborted) {
        throw context.signal.reason;
      }
      if (isWebrequestTimeoutError(error)) {
        throw error;
      }
      // Network failed, try cache
      if (context.logging) {
        console.log('[webrequest] Network failed, trying cache:', error);
      }

      const cachedEntry = await context.cacheStore.get(context.cacheKey, {
        signal: context.signal,
      });
      if (cachedEntry) {
        return {
          response: context.cacheStore.responseFromCacheEntry(cachedEntry),
          fromCache: true,
          revalidated: false,
        };
      }

      // No cache available, re-throw error
      throw error;
    }
  }

  private async cacheResponse(
    context: IStrategyContext,
    response: Response,
  ): Promise<void> {
    const metadata = extractCacheMetadata(response.headers);
    metadata.maxAge = resolveEffectiveMaxAge(
      metadata.maxAge,
      context.cacheMaxAge,
    );

    // Don't cache if no-store
    if (metadata.noStore) {
      return;
    }

    const entry = await context.cacheStore.cacheEntryFromResponse(
      context.request.url,
      response,
      metadata,
      {
        signal: context.signal,
        responseSignal: context.request.signal,
      },
    );
    await context.cacheStore.set(context.cacheKey, entry, {
      signal: context.signal,
      maxEntries: context.cacheMaxEntries,
    });
  }
}

/**
 * Cache-First Strategy
 * Check cache first, fetch if miss or stale
 */
export class CacheFirstStrategy implements ICacheStrategyHandler {
  async execute(context: IStrategyContext): Promise<IStrategyResult> {
    // Check cache first
    const cachedEntry = await context.cacheStore.get(context.cacheKey, {
      signal: context.signal,
    });

    if (cachedEntry) {
      const metadata = extractCacheMetadata(new Headers(cachedEntry.headers));

      // Check if cache is fresh
      if (
        isFreshForContext(context, cachedEntry, metadata) &&
        !requiresRevalidation(metadata)
      ) {
        if (context.logging) {
          console.log('[webrequest] Cache hit (fresh):', context.request.url);
        }

        return {
          response: context.cacheStore.responseFromCacheEntry(cachedEntry),
          fromCache: true,
          revalidated: false,
        };
      }

      // If requires revalidation, check with server
      if (
        requiresRevalidation(metadata) &&
        (cachedEntry.etag || cachedEntry.lastModified)
      ) {
        return await this.revalidate(context, cachedEntry);
      }
    }

    // Cache miss or stale, fetch from network
    if (context.logging) {
      console.log('[webrequest] Cache miss, fetching:', context.request.url);
    }

    const response = await context.fetchFn(context.request);

    // Cache the response
    const metadata = extractCacheMetadata(response.headers);
    metadata.maxAge = resolveEffectiveMaxAge(
      metadata.maxAge,
      context.cacheMaxAge,
    );
    if (!metadata.noStore) {
      try {
        const entry = await context.cacheStore.cacheEntryFromResponse(
          context.request.url,
          response,
          metadata,
          {
            signal: context.signal,
            responseSignal: context.request.signal,
          },
        );
        await context.cacheStore.set(context.cacheKey, entry, {
          signal: context.signal,
          maxEntries: context.cacheMaxEntries,
        });
      } catch (error) {
        await cancelDiscardedResponse(
          response,
          error,
          context.request.signal,
        );
        throw error;
      }
    }

    return {
      response,
      fromCache: false,
      revalidated: false,
    };
  }

  private async revalidate(
    context: IStrategyContext,
    cachedEntry: ICacheEntry,
  ): Promise<IStrategyResult> {
    const conditionalHeaders = createConditionalHeaders(cachedEntry);

    // Create a new request with conditional headers
    const revalidateRequest = new Request(context.request.url, {
      method: context.request.method,
      headers: {
        ...headersToObject(context.request.headers),
        ...conditionalHeaders,
      },
      signal: context.signal,
    });

    let response: Response | undefined;
    try {
      response = await context.fetchFn(revalidateRequest);

      // 304 Not Modified - cache is still valid
      if (response.status === 304) {
        if (context.logging) {
          console.log(
            '[webrequest] Cache revalidated (304):',
            context.request.url,
          );
        }

        // Update timestamp
        cachedEntry.timestamp = Date.now();
        await context.cacheStore.set(context.cacheKey, cachedEntry, {
          signal: context.signal,
          maxEntries: context.cacheMaxEntries,
        });
        await cancelDiscardedResponse(
          response,
          '304 response replaced by cached response',
          revalidateRequest.signal,
        );

        return {
          response: context.cacheStore.responseFromCacheEntry(cachedEntry),
          fromCache: true,
          revalidated: true,
        };
      }

      // Response changed, cache the new one
      if (response.ok) {
        const metadata = extractCacheMetadata(response.headers);
        metadata.maxAge = resolveEffectiveMaxAge(
          metadata.maxAge,
          context.cacheMaxAge,
        );
        if (!metadata.noStore) {
          const entry = await context.cacheStore.cacheEntryFromResponse(
            context.request.url,
            response,
            metadata,
            {
              signal: context.signal,
              responseSignal: revalidateRequest.signal,
            },
          );
          await context.cacheStore.set(context.cacheKey, entry, {
            signal: context.signal,
            maxEntries: context.cacheMaxEntries,
          });
        }
      }

      return {
        response,
        fromCache: false,
        revalidated: true,
      };
    } catch (error) {
      await cancelDiscardedResponse(
        response,
        error,
        revalidateRequest.signal,
      );
      if (context.signal?.aborted) {
        throw context.signal.reason;
      }
      if (isWebrequestTimeoutError(error)) {
        throw error;
      }
      // Revalidation failed, use cached response
      if (context.logging) {
        console.log('[webrequest] Revalidation failed, using cache:', error);
      }

      return {
        response: context.cacheStore.responseFromCacheEntry(cachedEntry),
        fromCache: true,
        revalidated: false,
      };
    }
  }
}

/**
 * Stale-While-Revalidate Strategy
 * Return cache immediately, update in background
 */
export class StaleWhileRevalidateStrategy implements ICacheStrategyHandler {
  async execute(context: IStrategyContext): Promise<IStrategyResult> {
    const cachedEntry = await context.cacheStore.get(context.cacheKey, {
      signal: context.signal,
    });

    if (cachedEntry) {
      // Return cached response immediately
      const cachedResponse =
        context.cacheStore.responseFromCacheEntry(cachedEntry);

      // Revalidate in background
      context.scheduleBackgroundRevalidation(async (signalArg) => {
        const backgroundContext: IStrategyContext = {
          ...context,
          request: new Request(context.request, { signal: signalArg }),
          signal: signalArg,
        };
        await this.revalidateInBackground(
          backgroundContext,
          cachedEntry,
        );
      });

      return {
        response: cachedResponse,
        fromCache: true,
        revalidated: false,
      };
    }

    // No cache, fetch from network
    const response = await context.fetchFn(context.request);

    // Cache the response
    const metadata = extractCacheMetadata(response.headers);
    metadata.maxAge = resolveEffectiveMaxAge(
      metadata.maxAge,
      context.cacheMaxAge,
    );
    if (!metadata.noStore && response.ok) {
      try {
        const entry = await context.cacheStore.cacheEntryFromResponse(
          context.request.url,
          response,
          metadata,
          {
            signal: context.signal,
            responseSignal: context.request.signal,
          },
        );
        await context.cacheStore.set(context.cacheKey, entry, {
          signal: context.signal,
          maxEntries: context.cacheMaxEntries,
        });
      } catch (error) {
        await cancelDiscardedResponse(
          response,
          error,
          context.request.signal,
        );
        throw error;
      }
    }

    return {
      response,
      fromCache: false,
      revalidated: false,
    };
  }

  private async revalidateInBackground(
    context: IStrategyContext,
    cachedEntry: ICacheEntry,
  ): Promise<void> {
    const metadata = extractCacheMetadata(new Headers(cachedEntry.headers));

    // Check if revalidation is needed
    if (
      isFreshForContext(context, cachedEntry, metadata) &&
      !requiresRevalidation(metadata)
    ) {
      return;
    }

    let response: Response | undefined;
    try {
      context.signal?.throwIfAborted();
      response = await context.fetchFn(context.request);

      if (response.ok) {
        const newMetadata = extractCacheMetadata(response.headers);
        newMetadata.maxAge = resolveEffectiveMaxAge(
          newMetadata.maxAge,
          context.cacheMaxAge,
        );
        if (!newMetadata.noStore) {
          const entry = await context.cacheStore.cacheEntryFromResponse(
            context.request.url,
            response,
            newMetadata,
            {
              signal: context.signal,
              responseSignal: context.request.signal,
              preserveResponse: false,
            },
          );
          await context.cacheStore.set(context.cacheKey, entry, {
            signal: context.signal,
            maxEntries: context.cacheMaxEntries,
          });

          if (context.logging) {
            console.log(
              '[webrequest] Background revalidation complete:',
              context.request.url,
            );
          }
        }
      }
    } catch (error) {
      if (context.signal?.aborted) {
        return;
      }
      // Background revalidation failed, keep existing cache
      if (context.logging) {
        console.warn('[webrequest] Background revalidation failed:', error);
      }
    } finally {
      await cancelDiscardedResponse(
        response,
        'background revalidation response discarded',
        context.request.signal,
      );
    }
  }
}

/**
 * Network-Only Strategy
 * Never use cache
 */
export class NetworkOnlyStrategy implements ICacheStrategyHandler {
  async execute(context: IStrategyContext): Promise<IStrategyResult> {
    const response = await context.fetchFn(context.request);

    return {
      response,
      fromCache: false,
      revalidated: false,
    };
  }
}

/**
 * Cache-Only Strategy
 * Only use cache, fail if miss
 */
export class CacheOnlyStrategy implements ICacheStrategyHandler {
  async execute(context: IStrategyContext): Promise<IStrategyResult> {
    const cachedEntry = await context.cacheStore.get(context.cacheKey, {
      signal: context.signal,
    });

    if (!cachedEntry) {
      throw new Error(
        `Cache miss for ${context.request.url} (cache-only mode)`,
      );
    }

    return {
      response: context.cacheStore.responseFromCacheEntry(cachedEntry),
      fromCache: true,
      revalidated: false,
    };
  }
}

const resolveEffectiveMaxAge = (
  responseMaxAgeArg: number,
  configuredMaxAgeArg?: number,
): number => {
  if (configuredMaxAgeArg === undefined) {
    return responseMaxAgeArg;
  }
  return Math.min(responseMaxAgeArg, configuredMaxAgeArg);
};

const isFreshForContext = (
  contextArg: IStrategyContext,
  cacheEntryArg: ICacheEntry,
  metadataArg: ICacheMetadata,
): boolean => {
  if (!isFresh(cacheEntryArg, metadataArg)) {
    return false;
  }
  if (contextArg.cacheMaxAge === undefined) {
    return true;
  }
  return (
    Date.now() - cacheEntryArg.timestamp < contextArg.cacheMaxAge
  );
};

const cancelDiscardedResponse = async (
  responseArg: Response | undefined,
  reasonArg: unknown,
  responseSignalArg?: AbortSignal,
): Promise<void> => {
  if (
    !responseArg?.body ||
    responseArg.bodyUsed ||
    responseSignalArg?.aborted
  ) {
    return;
  }
  await responseArg.body.cancel(reasonArg).catch(() => undefined);
};

/**
 * Get strategy handler for a given strategy type
 */
export function getStrategyHandler(
  strategy: TCacheStrategy,
): ICacheStrategyHandler {
  switch (strategy) {
    case 'network-first':
      return new NetworkFirstStrategy();
    case 'cache-first':
      return new CacheFirstStrategy();
    case 'stale-while-revalidate':
      return new StaleWhileRevalidateStrategy();
    case 'network-only':
      return new NetworkOnlyStrategy();
    case 'cache-only':
      return new CacheOnlyStrategy();
    default:
      return new NetworkFirstStrategy();
  }
}
